import { Component, effect, inject, signal, viewChild } from '@angular/core';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { lastValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import {
  WorkAbsence,
  WorkAbsencesV2Service,
  WorkAggregatedTimerecordBalanceDto,
  WorkPermissionsEnum,
  WorkStatus,
  WorkStatusSelectorEnum,
  WorkStatusV2Service,
  WorkTimerecordBalancesV2Service,
  WorkUpdateAbsenceV2Dto,
  WorkUsersV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import { ButtonComponent, LoadingSpinnerComponent } from '@work-frontend-monorepo/work-ui/layout';
import { DropdownOption, SelectComponent } from '@work-frontend-monorepo/work-ui/forms';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { TabContentDirective, TabOptions, TabsComponent } from '@work-frontend-monorepo/work-ui/navigation';
import { AlertDialogService } from '@work-frontend-monorepo/work-ui/modals';
import { AbsenzenFeriensaldoComponent } from '../../components/absenzen-feriensaldo/absenzen-feriensaldo.component';
import { AbsenzenCardComponent } from '../../components/absenzen-card/absenzen-card.component';
import { AbsenzenCreateUpdateModalComponent } from '../../components/absenzen-create-update-modal/absenzen-create-update-modal.component';

@Component({
  selector: 'sfg-app-absenzen-uebersicht-page',
  templateUrl: './absenzen-uebersicht-page.component.html',
  styleUrls: ['./absenzen-uebersicht-page.component.scss'],
  standalone: true,
  imports: [
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonTitle,
    IonToolbar,
    TranslatePipe,
    ButtonComponent,
    LoadingSpinnerComponent,
    SelectComponent,
    TabsComponent,
    TabContentDirective,
    AbsenzenFeriensaldoComponent,
    AbsenzenCardComponent,
    AbsenzenCreateUpdateModalComponent,
  ],
})
export class AbsenzenUebersichtPageComponent {
  // Viewchilds
  absenceModal = viewChild<AbsenzenCreateUpdateModalComponent>('absenceModal');

  // Injections
  private readonly store = inject(Store);
  private readonly workAbsencesV2Service = inject(WorkAbsencesV2Service);
  private readonly workTimerecordBalancesV2Service = inject(WorkTimerecordBalancesV2Service);
  private readonly workUsersV2Service = inject(WorkUsersV2Service);
  private readonly workStatusV2Service = inject(WorkStatusV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly alertDialogService = inject(AlertDialogService);

  // Profile
  profile = toSignal(this.store.select(ProfileState.profile));
  isAdmin = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.AbsenceADMIN)));
  canCreate = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.AbsenceCREATE)));
  canUpdate = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.AbsenceUPDATE)));
  canDelete = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.AbsenceDELETE)));

  // Tab Configuration
  tabs = signal<TabOptions[]>([]);
  activeTab = 'MeineAbsenzen';

  // My Absences
  myAbsences = signal<WorkAbsence[]>([]);
  myBalance = signal<WorkAggregatedTimerecordBalanceDto | undefined>(undefined);
  myLoading = signal(false);

  // Admin: All Employees
  adminAbsences = signal<WorkAbsence[]>([]);
  adminBalance = signal<WorkAggregatedTimerecordBalanceDto | undefined>(undefined);
  adminLoading = signal(false);
  userDropdownOptions = signal<DropdownOption[]>([]);
  selectedAdminUserId = signal<number | undefined>(undefined);

  // Approvals
  pendingAbsences = signal<WorkAbsence[]>([]);
  pendingLoading = signal(false);

  // Status options
  absenceStatuses = signal<WorkStatus[]>([]);

  private profileEffect = effect(() => {
    const profile = this.profile();
    const isAdmin = this.isAdmin();
    if (profile) {
      // Build tabs
      const tabOptions: TabOptions[] = [
        { title: 'MEINE_ABSENZEN', tabReference: 'MeineAbsenzen' },
      ];
      if (isAdmin) {
        tabOptions.push(
          { title: 'ALLE', tabReference: 'AlleMitarbeitenden', requiredPermission: WorkPermissionsEnum.AbsenceADMIN },
          { title: 'GENEHMIGUNGEN', tabReference: 'Genehmigungen', requiredPermission: WorkPermissionsEnum.AbsenceADMIN },
        );
      }
      this.tabs.set(tabOptions);

      // Load data (fire-and-forget, errors are handled in each method)
      this.initializeData(profile.id, isAdmin ?? false);
    }
  });

  private async initializeData(profileId: number, isAdmin: boolean) {
    await Promise.all([
      this.loadMyAbsences(profileId),
      this.loadBalance(profileId, 'my'),
      this.loadStatuses(),
      ...(isAdmin ? [this.loadUserDropdown()] : []),
    ]);
  }

  protected async loadMyAbsences(userId: number) {
    this.myLoading.set(true);
    try {
      const absences = await lastValueFrom(
        this.workAbsencesV2Service.absencesV2ControllerFindByUserId({ userId }),
      );
      this.myAbsences.set(absences);
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadMyAbsencesException', error);
    }
    this.myLoading.set(false);
  }

  protected async loadAdminAbsences(userId: number) {
    this.adminLoading.set(true);
    try {
      const absences = await lastValueFrom(
        this.workAbsencesV2Service.absencesV2ControllerFindByUserId({ userId }),
      );
      this.adminAbsences.set(absences);
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadAdminAbsencesException', error);
    }
    this.adminLoading.set(false);
  }

  protected async loadBalance(userId: number, target: 'my' | 'admin') {
    try {
      const balance = await lastValueFrom(
        this.workTimerecordBalancesV2Service.timerecordBalancesV2ControllerFindAggregatedByUserIdAndYear({
          userId,
          year: dayjs().year(),
        }),
      );
      if (target === 'my') {
        this.myBalance.set(balance);
      } else {
        this.adminBalance.set(balance);
      }
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadBalanceException', error);
    }
  }

  protected async loadStatuses() {
    try {
      const statuses = await lastValueFrom(
        this.workStatusV2Service.statusV2ControllerGetAllBySelector({ selector: WorkStatusSelectorEnum.Absences }),
      );
      this.absenceStatuses.set(statuses);
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadStatusesException', error);
    }
  }

  protected async loadUserDropdown() {
    try {
      const users = await lastValueFrom(this.workUsersV2Service.usersV2ControllerGetAll());
      this.userDropdownOptions.set(
        users.map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id, name: `${u.firstName} ${u.lastName}` })),
      );
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadUserDropdownException', error);
    }
  }

  protected async loadPendingAbsences() {
    if (!this.isAdmin()) return;
    this.pendingLoading.set(true);
    try {
      // Load all users' dropdown options and fetch absences for each
      const users = this.userDropdownOptions();
      const allPending: WorkAbsence[] = [];
      for (const user of users) {
        try {
          const absences = await lastValueFrom(
            this.workAbsencesV2Service.absencesV2ControllerFindByUserId({ userId: user.value as number }),
          );
          const pending = absences.filter((a) => a.status?.name === 'Offen');
          allPending.push(...pending);
        } catch {
          // Skip users we can't access
        }
      }
      this.pendingAbsences.set(allPending);
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadPendingAbsencesException', error);
    }
    this.pendingLoading.set(false);
  }

  protected async onAdminUserChange(userId: number) {
    this.selectedAdminUserId.set(userId);
    await this.loadAdminAbsences(userId);
    await this.loadBalance(userId, 'admin');
  }

  protected onTabChange(tab: string) {
    this.activeTab = tab;
    if (tab === 'Genehmigungen') {
      this.loadPendingAbsences();
    }
  }

  protected openCreateModal() {
    const userId = this.activeTab === 'AlleMitarbeitenden' ? this.selectedAdminUserId() : this.profile()?.id;
    if (userId) {
      this.absenceModal()?.openModal();
    }
  }

  protected openEditModal(absence: WorkAbsence) {
    this.absenceModal()?.openModal(absence);
  }

  protected async deleteAbsence(absence: WorkAbsence) {
    try {
      await lastValueFrom(this.workAbsencesV2Service.absencesV2ControllerDelete({ id: absence.id }));
      this.alertDialogService.showAlert({ title: 'EINTRAG_LOESCHEN_ERFOLG', feedbackState: 'success' });
      await this.refreshCurrentView();
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('deleteAbsenceException', error);
    }
  }

  protected async approveAbsence(absence: WorkAbsence) {
    const approvedStatus = this.absenceStatuses().find((s) => s.name === 'Genehmigt');
    if (approvedStatus) {
      await this.changeAbsenceStatus(absence, approvedStatus.id);
    }
  }

  protected async rejectAbsence(absence: WorkAbsence) {
    const rejectedStatus = this.absenceStatuses().find((s) => s.name === 'Abgelehnt');
    if (rejectedStatus) {
      await this.changeAbsenceStatus(absence, rejectedStatus.id);
    }
  }

  protected async onAbsenceEdited() {
    await this.refreshCurrentView();
  }

  protected getCurrentUserId(): number {
    if (this.activeTab === 'AlleMitarbeitenden' && this.selectedAdminUserId()) {
      return this.selectedAdminUserId()!;
    }
    return this.profile()?.id ?? 0;
  }

  private async changeAbsenceStatus(absence: WorkAbsence, statusId: number) {
    try {
      const updateDto: WorkUpdateAbsenceV2Dto = {
        start: absence.start!,
        end: absence.end!,
        percentage: absence.percentage,
        timerecordtypeId: absence.timerecordtypeId!,
        createdBy: absence.createdBy!,
        statusId,
      };
      await lastValueFrom(
        this.workAbsencesV2Service.absencesV2ControllerUpdate({
          id: absence.id,
          body: updateDto,
        }),
      );
      this.alertDialogService.showAlert({ title: 'AENDERUNG_SPEICHERN_ERFOLGREICH', feedbackState: 'success' });
      await this.refreshCurrentView();
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('changeStatusException', error);
    }
  }

  private async refreshCurrentView() {
    const profile = this.profile();
    if (!profile) return;

    if (this.activeTab === 'MeineAbsenzen') {
      await this.loadMyAbsences(profile.id);
      await this.loadBalance(profile.id, 'my');
    } else if (this.activeTab === 'AlleMitarbeitenden' && this.selectedAdminUserId()) {
      await this.loadAdminAbsences(this.selectedAdminUserId()!);
      await this.loadBalance(this.selectedAdminUserId()!, 'admin');
    } else if (this.activeTab === 'Genehmigungen') {
      await this.loadPendingAbsences();
    }
  }
}
